import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ClipboardList, DollarSign, Store, RefreshCw, TrendingUp, ShieldAlert, ArrowRight } from 'lucide-react';
import { adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import RevenueDashboard from './RevenueDashboard';

const STATUS_COLORS = {
  placed: 'bg-slate-100 text-slate-700',
  confirmed: 'bg-blue-50 text-blue-700',
  preparing: 'bg-amber-50 text-amber-700',
  on_the_way: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-rose-50 text-rose-700',
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setStats(await adminApi.stats()); } catch { /* */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Live operations overview — auto-refreshes every 30s</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total users" value={stats?.users?.total ?? '—'} accent="bg-blue-50 text-blue-700" />
        <StatCard icon={Store} label="Active vendors" value={`${stats?.vendors?.active ?? '—'} / ${stats?.vendors?.total ?? '—'}`} accent="bg-emerald-50 text-emerald-700" />
        <StatCard icon={ClipboardList} label="Orders today" value={stats?.orders?.today ?? '—'} sub={`${stats?.orders?.active ?? 0} active`} accent="bg-amber-50 text-amber-700" />
        <StatCard icon={DollarSign} label="Revenue today" value={fmt(stats?.revenue?.today)} sub={`${fmt(stats?.revenue?.last_7d)} · last 7d`} accent="bg-rose-50 text-rose-700" />
      </div>

      {(stats?.vendors?.pending ?? 0) > 0 && (
        <Link
          to="/admin/vendors?filter=pending"
          data-testid="pending-vendors-widget"
          className="mt-4 group flex items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/40 p-4 hover:shadow-md hover:border-amber-300 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold text-amber-900">
                {stats.vendors.pending} vendor{stats.vendors.pending === 1 ? '' : 's'} awaiting approval
              </div>
              <div className="text-xs text-amber-800/80 mt-0.5">
                New stores can't sell until you approve them — click to review.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-amber-900 font-medium text-sm">
            Review now
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </Link>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Top vendors (30d)</h2>
          </div>
          {(!stats || stats.top_vendors?.length === 0) ? (
            <div className="text-sm text-slate-500 py-6 text-center">No order data yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {stats.top_vendors.map((v, idx) => (
                <div key={v.id} className="py-3 flex items-center gap-4">
                  <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center text-sm font-semibold">{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{v.name}</div>
                    <div className="text-xs text-slate-500">{v.orders} orders</div>
                  </div>
                  <div className="font-semibold text-slate-900">{fmt(v.revenue)}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-semibold text-slate-900">Order status</h2>
          <div className="mt-3 space-y-2">
            {stats && Object.keys(stats.orders.status_breakdown || {}).length === 0 && (
              <div className="text-sm text-slate-500 py-4 text-center">No orders yet.</div>
            )}
            {stats && Object.entries(stats.orders.status_breakdown || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <Badge className={`${STATUS_COLORS[k] || 'bg-slate-100 text-slate-700'} border-0 hover:opacity-100`}>{k.replace(/_/g, ' ')}</Badge>
                <span className="font-semibold text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="mt-8">
        <RevenueDashboard />
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`h-10 w-10 rounded-xl ${accent} flex items-center justify-center`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default Dashboard;
